import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Users, UserPlus, Search, Trash2, X, Upload,
  Mail, Phone, Loader, User, Pencil, BookOpen,
  Plus, GraduationCap, Briefcase, Check, Shield, Save,
} from "lucide-react";
import {
  lmsGetTeachers, lmsCreateTeacher, lmsUpdateTeacher, lmsDeleteTeacher,
  lmsGetTeacherClassSubjects, lmsAssignTeacherClassSubject, lmsRemoveTeacherClassSubject,
  lmsGetClassSubjects, lmsGetTeacherPermissions, lmsSaveTeacherPermissions,
} from "../api/lmsApi";
import "../lms.css";

const getSchoolData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    return { school_id: ud.admin_id || ud.school_id || ud.id || null, ...ud };
  } catch { return { school_id: null }; }
};

function mkForm() {
  return {
    name: "", email: "", phone: "",
    qualification: "", specialization: "", experience_years: "",
    profile_photo: null,
  };
}

/* ── Confirm Modal ── */
function ConfirmModal({ message, onConfirm, onCancel, loading }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div className="ms2-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-confirm-icon"><Trash2 size={24} color="#dc2626" /></div>
        <p className="ms2-confirm-msg">{message}</p>
        <div className="ms2-confirm-footer">
          <button className="lms-btn-ghost" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="ms2-confirm-delete-btn" onClick={onConfirm} disabled={loading}>
            {loading ? <Loader size={13} className="ms2-spin" /> : <Trash2 size={13} />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Teacher Form Modal ── */
function TeacherModal({ teacher, schoolId, onClose, onSaved }) {
  const [form, setForm] = useState(teacher ? {
    name: teacher.name || "",
    email: teacher.email || "",
    phone: teacher.phone || "",
    qualification: teacher.qualification || "",
    specialization: teacher.specialization || "",
    experience_years: teacher.experience_years || "",
    profile_photo: null,
  } : mkForm());
  const [photoPreview, setPhotoPreview] = useState(teacher?.profile_photo || null);
  const [saving, setSaving] = useState(false);
  const photoRef = React.useRef(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    set("profile_photo", file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("school_id", schoolId);
      fd.append("name", form.name.trim());
      if (form.email)          fd.append("email", form.email);
      if (form.phone)          fd.append("phone", form.phone);
      if (form.qualification)  fd.append("qualification", form.qualification);
      if (form.specialization) fd.append("specialization", form.specialization);
      if (form.experience_years) fd.append("experience_years", form.experience_years);
      if (form.profile_photo)  fd.append("profile_photo", form.profile_photo);

      if (teacher) {
        await lmsUpdateTeacher(teacher.id, fd);
        toast.success("Teacher updated successfully");
      } else {
        await lmsCreateTeacher(fd);
        toast.success("Teacher created successfully");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save teacher");
    } finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="crs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crs-modal-header">
          <span>{teacher ? "Edit Teacher" : "Add New Teacher"}</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="crs-modal-body">
          {/* Photo row */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 4 }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%", flexShrink: 0,
              border: "2px dashed #cbd5e1", background: "#f8fafc",
              overflow: "hidden", display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer",
            }} onClick={() => photoRef.current?.click()}>
              {photoPreview
                ? <img src={photoPreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <User size={28} color="#94a3b8" />}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4 }}>Profile Photo</div>
              <label style={{
                fontSize: 12, fontWeight: 600, color: "#1c498d",
                background: "#eff6ff", border: "1px solid #bfdbfe",
                padding: "5px 12px", borderRadius: 6, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}>
                <Upload size={12} /> Upload Photo
                <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} />
              </label>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>JPG, PNG, WEBP — max 5MB</div>
            </div>
          </div>

          {/* Full Name */}
          <div className="crs-field">
            <label>Full Name <span className="crs-req">*</span></label>
            <input type="text" placeholder="e.g. Rahul Verma"
              value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>

          {/* Email + Phone */}
          <div className="crs-grid2">
            <div className="crs-field">
              <label>Email</label>
              <input type="email" placeholder="teacher@school.com"
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="crs-field">
              <label>Phone</label>
              <input type="tel" placeholder="Mobile number"
                value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
          </div>

          {/* Qualification + Specialization */}
          <div className="crs-grid2">
            <div className="crs-field">
              <label>Qualification</label>
              <input type="text" placeholder="e.g. B.Ed, M.Sc"
                value={form.qualification} onChange={(e) => set("qualification", e.target.value)} />
            </div>
            <div className="crs-field">
              <label>Specialization</label>
              <input type="text" placeholder="e.g. Mathematics"
                value={form.specialization} onChange={(e) => set("specialization", e.target.value)} />
            </div>
          </div>

          {/* Experience */}
          <div className="crs-field" style={{ maxWidth: 200 }}>
            <label>Experience (Years)</label>
            <input type="number" min="0" placeholder="e.g. 5"
              value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)} />
          </div>
        </div>

        <div className="crs-modal-footer">
          <button className="crs-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="crs-btn-save" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? <Loader size={13} className="ms2-spin" /> : null}
            {teacher ? "Save Changes" : "Add Teacher"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Class-Subject Assignment Modal ── */
function AssignClassSubjectModal({ teacher, schoolId, onClose, onSaved }) {
  const [allCs, setAllCs]       = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      lmsGetClassSubjects({ school_id: schoolId }),
      lmsGetTeacherClassSubjects(teacher.id),
    ]).then(([csRes, assignedRes]) => {
      setAllCs(csRes.data.data || []);
      setAssigned((assignedRes.data.data || []).map((a) => a.class_subject_id));
    }).catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, [teacher.id, schoolId]);

  const toggle = async (csId) => {
    setSaving(true);
    try {
      if (assigned.includes(csId)) {
        await lmsRemoveTeacherClassSubject(teacher.id, csId);
        setAssigned((p) => p.filter((x) => x !== csId));
        toast.success("Assignment removed");
      } else {
        await lmsAssignTeacherClassSubject(teacher.id, { class_subject_id: csId, school_id: schoolId });
        setAssigned((p) => [...p, csId]);
        toast.success("Class-subject assigned");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update assignment");
    } finally { setSaving(false); }
  };

  // Group class-subjects by class name
  const grouped = allCs.reduce((acc, cs) => {
    if (!acc[cs.class_name]) acc[cs.class_name] = [];
    acc[cs.class_name].push(cs);
    return acc;
  }, {});

  const assignedCount = assigned.length;

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div style={{
        width: "100%", maxWidth: 560, background: "#fff", borderRadius: 12,
        boxShadow: "0 20px 40px rgba(0,0,0,0.15)", display: "flex",
        flexDirection: "column", maxHeight: "85vh", overflow: "hidden",
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding: "18px 20px", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
              Assign Class &amp; Subjects
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {teacher.name} &nbsp;·&nbsp;
              <span style={{ color: "#4f46e5", fontWeight: 600 }}>{assignedCount} assigned</span>
            </div>
          </div>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Loader size={22} className="ms2-spin" />
            </div>
          ) : allCs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 14 }}>
              No class-subjects found. Set them up in Master Setup first.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {Object.entries(grouped).map(([className, subjects]) => (
                <div key={className}>
                  {/* Class label */}
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: "#6b7280",
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    marginBottom: 8, paddingLeft: 2,
                  }}>
                    {className}
                  </div>
                  {/* Subject chips */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {subjects.map((cs) => {
                      const isAssigned = assigned.includes(cs.id);
                      return (
                        <button
                          key={cs.id}
                          onClick={() => toggle(cs.id)}
                          disabled={saving}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "7px 14px", borderRadius: 20, fontSize: 13,
                            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
                            border: `1.5px solid ${isAssigned ? "#4f46e5" : "#d1d5db"}`,
                            background: isAssigned ? "#4f46e5" : "#f9fafb",
                            color: isAssigned ? "#fff" : "#374151",
                            transition: "all 0.15s",
                          }}
                        >
                          {isAssigned ? <BookOpen size={12} /> : <Plus size={12} />}
                          {cs.subject_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: "1px solid #e5e7eb",
          display: "flex", justifyContent: "flex-end", flexShrink: 0,
        }}>
          <button className="crs-btn-cancel" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── Permission Drawer ── */
const MODULES = [
  { key: "live_classes", label: "Live Classes",         icon: "🎥", hasCreate: true },
  { key: "exams",        label: "Exams",                icon: "📋", hasCreate: true },
  { key: "courses",      label: "Courses",              icon: "📚", hasCreate: false },
  { key: "attendance",   label: "Attendance",           icon: "✅", hasCreate: true },
  { key: "audit_log",    label: "Live Class Audit Log", icon: "📊", hasCreate: false },
  { key: "recordings",   label: "Recordings",           icon: "🎬", hasCreate: false },
];
const DEFAULT_PERMS = () =>
  Object.fromEntries(MODULES.map((m) => [m.key, { can_view: false, can_create: false }]));

function PermissionDrawer({ teacher, schoolId, onClose }) {
  const [permissions, setPermissions] = useState(DEFAULT_PERMS());
  const [fetching,    setFetching]    = useState(true);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (!teacher) return;
    setFetching(true);
    lmsGetTeacherPermissions(teacher.id, schoolId)
      .then((res) => {
        const raw = res.data?.data || {};
        const merged = DEFAULT_PERMS();
        MODULES.forEach((m) => {
          if (raw[m.key]) merged[m.key] = { can_view: !!raw[m.key].can_view, can_create: !!raw[m.key].can_create };
        });
        setPermissions(merged);
      })
      .catch(() => setPermissions(DEFAULT_PERMS()))
      .finally(() => setFetching(false));
  }, [teacher?.id, schoolId]);

  const toggle = (mod, field) =>
    setPermissions((prev) => ({ ...prev, [mod]: { ...prev[mod], [field]: !prev[mod][field] } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await lmsSaveTeacherPermissions(teacher.id, { school_id: schoolId, permissions });
      toast.success("Permissions saved");
      onClose();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 560, maxWidth: "95vw",
        maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <div className="std-avatar-placeholder" style={{ background: "linear-gradient(135deg,#4f46e5,#a855f7)", color: "#fff", fontSize: 15 }}>
            {getInitials(teacher.name)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{teacher.name}</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>Module Permissions</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {fetching ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <Loader size={24} className="ms2-spin" color="#4f46e5" />
            </div>
          ) : (
            <table className="std-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Module</th>
                  <th style={{ textAlign: "center", width: 130 }}>View All</th>
                  <th style={{ textAlign: "center", width: 130 }}>Create / Edit</th>
                </tr>
              </thead>
              <tbody>
                {MODULES.map((mod) => (
                  <tr key={mod.key}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 18 }}>{mod.icon}</span>
                        <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{mod.label}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", lineHeight: 1 }}>
                        <input type="checkbox" checked={permissions[mod.key]?.can_view || false}
                          onChange={() => toggle(mod.key, "can_view")}
                          style={{ width: 15, height: 15, accentColor: "#4f46e5", cursor: "pointer", marginRight: 6, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: permissions[mod.key]?.can_view ? "#4f46e5" : "#94a3b8" }}>
                          {permissions[mod.key]?.can_view ? "All" : "Own only"}
                        </span>
                      </label>
                    </td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      {mod.hasCreate ? (
                        <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", lineHeight: 1 }}>
                          <input type="checkbox" checked={permissions[mod.key]?.can_create || false}
                            onChange={() => toggle(mod.key, "can_create")}
                            style={{ width: 15, height: 15, accentColor: "#4f46e5", cursor: "pointer", marginRight: 6, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: permissions[mod.key]?.can_create ? "#4f46e5" : "#94a3b8" }}>
                            {permissions[mod.key]?.can_create ? "Allowed" : "Restricted"}
                          </span>
                        </label>
                      ) : (
                        <span style={{ color: "#d1d5db", fontSize: 13 }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: "14px 20px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button className="crs-btn-cancel" onClick={onClose}>Cancel</button>
          <button onClick={handleSave} disabled={saving || fetching} className="std-btn-primary">
            {saving ? <Loader size={13} className="ms2-spin" /> : <Save size={13} />}
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   TEACHERS PAGE
══════════════════════════════════════════ */
export default function Teachers() {
  const { school_id: schoolId } = getSchoolData();

  const [teachers, setTeachers]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [showModal, setShowModal]     = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [assignModal, setAssignModal]     = useState(null);
  const [permModal,   setPermModal]       = useState(null);
  const [confirm, setConfirm]             = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const res = await lmsGetTeachers({ school_id: schoolId });
      setTeachers(res.data.data || []);
    } catch { toast.error("Failed to load teachers"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTeachers(); }, [schoolId]);

  const handleEdit = (t) => { setEditTeacher(t); setShowModal(true); };

  const handleDelete = (t) => {
    setConfirm({
      message: `Are you sure you want to delete teacher "${t.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        await lmsDeleteTeacher(t.id);
        toast.success("Teacher deleted");
        fetchTeachers();
      },
    });
  };

  const filtered = teachers.filter((t) =>
    !search ||
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase()) ||
    t.specialization?.toLowerCase().includes(search.toLowerCase())
  );

  const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div className="std-page-header">
        <div>
          <h1 className="std-page-title">Teachers</h1>
          <p className="std-page-subtitle">Manage teacher profiles and class-subject assignments</p>
        </div>
        <button className="std-btn-primary" onClick={() => { setEditTeacher(null); setShowModal(true); }}>
          <UserPlus size={16} /> Add Teacher
        </button>
      </div>

      {/* Stats */}
      <div className="std-stats-grid">
        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#eff6ff", color: "#1c498d" }}>
            <Users size={22} />
          </div>
          <div>
            <div className="std-stat-label">Total Teachers</div>
            <div className="std-stat-value">{teachers.length}</div>
          </div>
        </div>
        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#f0fdf4", color: "#166534" }}>
            <BookOpen size={22} />
          </div>
          <div>
            <div className="std-stat-label">Active Teachers</div>
            <div className="std-stat-value">{teachers.filter((t) => t.status !== 0).length}</div>
          </div>
        </div>
        <div className="std-stat-card">
          <div className="std-stat-icon-wrap" style={{ background: "#fffbeb", color: "#b45309" }}>
            <GraduationCap size={22} />
          </div>
          <div>
            <div className="std-stat-label">Specializations</div>
            <div className="std-stat-value" style={{ fontSize: 18 }}>
              {new Set(teachers.map((t) => t.specialization).filter(Boolean)).size || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="std-toolbar-card">
        <div className="std-search-box">
          <Search size={16} className="std-search-icon" />
          <input
            type="text" className="std-search-input"
            placeholder="Search by name, email or specialization..."
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
          {filtered.length} of {teachers.length} Teachers
        </span>
      </div>

      {/* Table */}
      <div className="std-table-card">
        <table className="std-table">
          <thead>
            <tr>
              <th>Teacher</th>
              <th>Contact</th>
              <th>Qualification</th>
              <th>Specialization</th>
              <th>Experience</th>
              <th>Class Subjects</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: 40 }}>
                  <Loader size={24} className="ms2-spin" style={{ margin: "0 auto 8px" }} />
                  <div style={{ color: "#64748b", fontSize: 14 }}>Loading teachers...</div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "48px 20px" }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: "#f1f5f9", color: "#94a3b8",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 12px",
                  }}>
                    <User size={28} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#334155" }}>No teachers found</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
                    Click "Add Teacher" to get started.
                  </div>
                </td>
              </tr>
            ) : filtered.map((t) => (
              <tr key={t.id}>
                {/* Teacher info */}
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {t.profile_photo ? (
                      <img src={t.profile_photo} alt={t.name} className="std-avatar" />
                    ) : (
                      <div className="std-avatar-placeholder">{getInitials(t.name)}</div>
                    )}
                    <div>
                      <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{t.name}</div>
                      {t.school_name && (
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>{t.school_name}</div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Contact */}
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {t.email && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#475569", fontSize: 13 }}>
                        <Mail size={13} style={{ color: "#94a3b8" }} />{t.email}
                      </div>
                    )}
                    {t.phone && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#475569", fontSize: 13 }}>
                        <Phone size={13} style={{ color: "#94a3b8" }} />{t.phone}
                      </div>
                    )}
                  </div>
                </td>

                {/* Qualification */}
                <td>
                  {t.qualification ? (
                    <span className="std-badge-class">{t.qualification}</span>
                  ) : <span style={{ color: "#d1d5db" }}>—</span>}
                </td>

                {/* Specialization */}
                <td>
                  {t.specialization ? (
                    <span className="std-badge-adm" style={{ background: "#eff6ff", color: "#1c498d", borderColor: "#bfdbfe" }}>
                      {t.specialization}
                    </span>
                  ) : <span style={{ color: "#d1d5db" }}>—</span>}
                </td>

                {/* Experience */}
                <td>
                  {t.experience_years != null ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#475569" }}>
                      <Briefcase size={13} style={{ color: "#94a3b8" }} />
                      {t.experience_years} yr{t.experience_years !== 1 ? "s" : ""}
                    </div>
                  ) : <span style={{ color: "#d1d5db" }}>—</span>}
                </td>

                {/* Assign class-subjects */}
                <td>
                  <button
                    onClick={() => setAssignModal(t)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "5px 12px", borderRadius: 6, border: "1px solid #e5e7eb",
                      background: "#f9fafb", color: "#374151", fontSize: 12,
                      fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    <BookOpen size={12} /> Manage
                  </button>
                </td>

                {/* Actions */}
                <td style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                    <button
                      onClick={() => setPermModal(t)}
                      title="Set Permissions"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "5px 12px", borderRadius: 6,
                        border: "1px solid #4f46e5", background: "#eff6ff",
                        color: "#4f46e5", fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      <Shield size={12} /> Permissions
                    </button>
                    <button className="crs-action-btn crs-action-edit" onClick={() => handleEdit(t)} title="Edit">
                      <Pencil size={14} />
                    </button>
                    <button className="std-btn-delete" onClick={() => handleDelete(t)} title="Delete">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Teacher form modal */}
      {showModal && (
        <TeacherModal
          teacher={editTeacher}
          schoolId={schoolId}
          onClose={() => { setShowModal(false); setEditTeacher(null); }}
          onSaved={fetchTeachers}
        />
      )}

      {/* Permissions modal */}
      {permModal && (
        <PermissionDrawer
          teacher={permModal}
          schoolId={schoolId}
          onClose={() => setPermModal(null)}
        />
      )}

      {/* Assign class-subject modal */}
      {assignModal && (
        <AssignClassSubjectModal
          teacher={assignModal}
          schoolId={schoolId}
          onClose={() => setAssignModal(null)}
          onSaved={fetchTeachers}
        />
      )}

      {/* Confirm delete */}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={async () => {
            setConfirmLoading(true);
            try { await confirm.onConfirm(); }
            finally { setConfirmLoading(false); setConfirm(null); }
          }}
          onCancel={() => setConfirm(null)}
          loading={confirmLoading}
        />
      )}
    </div>
  );
}
